import { supabase } from './supabaseClient';

/**
 * Calculate milestone info based on closed deals count
 * @param {number} closedDealsCount - Total number of closed deals
 * @returns {object} Milestone information including progress
 */
export function calculateMilestoneInfo(closedDealsCount) {
  const milestones = [];
  let level = 0;
  
  // Generate milestones up to current deal count + next milestone
  while (true) {
    const milestoneDeals = 5 + (level * 10);
    const reward = 100 + (level * 50);
    
    milestones.push({
      deals: milestoneDeals,
      reward: reward,
      reached: closedDealsCount >= milestoneDeals,
    });
    
    // Stop after we pass current count by one milestone
    if (milestoneDeals > closedDealsCount + 10) break;
    level++;
  }
  
  // Find current and next milestone
  const currentMilestone = milestones
    .filter(m => m.reached)
    .pop() || null;
    
  const nextMilestone = milestones
    .find(m => !m.reached) || null;
  
  // Calculate progress to next milestone
  let progress = 0;
  if (nextMilestone) {
    const prevMilestoneDeals = currentMilestone ? currentMilestone.deals : 0;
    progress = ((closedDealsCount - prevMilestoneDeals) / (nextMilestone.deals - prevMilestoneDeals)) * 100;
    progress = Math.min(100, Math.max(0, progress));
  } else {
    progress = 100;
  }
  
  return {
    closedDeals: closedDealsCount,
    currentMilestone,
    nextMilestone,
    progress,
    allMilestones: milestones,
  };
}

/**
 * Load claimed milestones for a user
 * @param {string} userId - User ID
 * @returns {Array} Array of claimed milestone rewards
 */
export async function loadClaimedMilestones(userId) {
  if (!supabase || !userId) return [];
  
  try {
    const { data, error } = await supabase
      .from('milestone_rewards')
      .select('*')
      .eq('user_id', userId)
      .order('milestone_deals', { ascending: true });
    
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.error('Error loading milestone rewards:', err);
    return [];
  }
}

/**
 * Calculate total milestone earnings
 * @param {Array} claimedMilestones - Array of claimed milestone records
 * @returns {number} Total earnings from milestones
 */
export function calculateTotalMilestoneEarnings(claimedMilestones) {
  return claimedMilestones.reduce((total, m) => total + parseFloat(m.reward_amount), 0);
}
